import { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { CheckCircle2, AlertCircle, Loader2, Check, X, ShieldCheck } from 'lucide-react';
import { Button } from '../components_dashboard/ui/button';

export default function CreateAccount() {
  const { token } = useAuth();
  
  const [step, setStep] = useState('create'); // 'create' | 'otp'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    dashboard_type: 'platform',
  });
  
  const [status, setStatus] = useState({ type: null, message: '' });
  const [loading, setLoading] = useState(false);
  
  // OTP state
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const otpRefs = useRef([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Validation rules
  const hasLowerUpper = /(?=.*[a-z])(?=.*[A-Z])/.test(formData.password);
  const hasNumberOrSymbol = /(?=.*[0-9!@#$%^&*()_+{}:"<>?|~`\-\=[\]\\;',./])/.test(formData.password);
  const isMinLength = formData.password.length >= 8;
  const noSpacesOrPipe = formData.password.length > 0 && !/[\s|]/.test(formData.password);

  const canSubmit = formData.name && formData.email && hasLowerUpper && hasNumberOrSymbol && isMinLength && noSpacesOrPipe && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: null, message: '' });
    setLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      
      // 1. Create the user
      const response = await fetch(`${apiUrl}/api/v1/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: 'member',
          dashboard_type: formData.dashboard_type,
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.detail || 'Failed to create account');
      }

      // 2. Send verification OTP
      const otpResponse = await fetch(`${apiUrl}/api/v1/admin/users/send-verification-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email: formData.email }),
      });

      if (!otpResponse.ok) {
        const errData = await otpResponse.json().catch(() => null);
        throw new Error(errData?.detail || 'Account created, but failed to send OTP');
      }

      setStep('otp');
      setStatus({ type: 'success', message: 'Account created! Verification OTP sent to user.' });
    } catch (err) {
      console.error(err);
      setStatus({ type: 'error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setOtpError('');

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(''));
      otpRefs.current[5]?.focus();
    }
  };

  const handleVerifyOtp = async (e) => {
    if (e) e.preventDefault();
    const otpStr = otp.join('');
    if (otpStr.length !== 6) {
      setOtpError('Please enter all 6 digits');
      return;
    }
    setOtpError('');
    setOtpLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/api/v1/admin/users/verify-email-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email: formData.email, otp: otpStr }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.detail || 'Failed to verify OTP');
      }

      setStatus({ type: 'success', message: 'Account verified successfully!' });
      setFormData({ name: '', email: '', password: '', dashboard_type: 'platform' });
      setOtp(['', '', '', '', '', '']);
      setStep('create');
    } catch (err) {
      setOtpError(err.message);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setOtpLoading(true);
    setOtpError('');
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/api/v1/admin/users/send-verification-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email: formData.email }),
      });
      if (!response.ok) throw new Error('Failed to resend OTP');
      setStatus({ type: 'success', message: 'OTP resent successfully!' });
    } catch (err) {
      setOtpError(err.message);
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col p-6">
      
      <div className="flex flex-col flex-1 h-full max-w-full overflow-hidden transition-all duration-300 ml-0 ">
        <header className="flex-shrink-0 px-6 py-4 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-50">
                <span className="text-sm font-semibold text-indigo-600 uppercase">Admin Controls</span>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 p-6 overflow-y-auto">
          <div className="max-w-2xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold tracking-tight text-slate-900">Create Account</h1>
              <p className="mt-2 text-sm text-slate-500">
                Provision a new user account on the platform. The user will be created with the default "member" role and must be verified.
              </p>
            </div>

            <div className="p-6 bg-white border shadow-sm rounded-xl border-slate-200">
              {status.message && (
                <div className={`flex items-center gap-3 p-4 mb-6 rounded-lg ${
                  status.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                }`}>
                  {status.type === 'success' ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  )}
                  <p className="text-sm font-medium">{status.message}</p>
                </div>
              )}

              {step === 'create' ? (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-slate-700">Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 bg-white border rounded-lg border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="E.g. John Doe"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-slate-700">Email ID</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 bg-white border rounded-lg border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="E.g. john@example.com"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-slate-700">Password</label>
                    <div className="relative">
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        className={`w-full px-4 py-2.5 mb-2 bg-white border rounded-lg focus:outline-none focus:ring-2 pr-10 ${
                          formData.password.length > 0
                            ? (hasLowerUpper && hasNumberOrSymbol && isMinLength && noSpacesOrPipe
                              ? 'border-emerald-500 focus:ring-emerald-500 focus:border-emerald-500 shadow-[0_0_0_1px_#10b981]'
                              : 'border-red-500 focus:ring-red-500 focus:border-red-500 shadow-[0_0_0_1px_#ef4444]')
                            : 'border-slate-300 focus:ring-indigo-500 focus:border-indigo-500'
                        }`}
                        placeholder="Enter a strong password"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pb-2">
                        {formData.password.length > 0 && (hasLowerUpper && hasNumberOrSymbol && isMinLength && noSpacesOrPipe ? (
                          <Check className="w-5 h-5 text-emerald-500" />
                        ) : (
                          <X className="w-5 h-5 text-red-500" />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-slate-700">Dashboard Access</label>
                    <select
                      name="dashboard_type"
                      value={formData.dashboard_type}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 bg-white border rounded-lg border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="platform">TechLadder Platform (Default)</option>
                      <option value="clinicalsim">ClinicalSim (Medical Training)</option>
                    </select>
                  </div>

                  {/* Password Rules */}
                  <div className="text-[13px] text-slate-500 mb-2 font-bold">
                    Password Must:
                  </div>
                  <div className="pl-1 space-y-1">
                    <div className={`text-xs flex items-center gap-1.5 transition-colors ${formData.password.length > 0 && hasLowerUpper ? 'text-emerald-600' : 'text-slate-500'}`}>
                      - Include lower and upper characters
                    </div>
                    <div className={`text-xs flex items-center gap-1.5 transition-colors ${formData.password.length > 0 && hasNumberOrSymbol ? 'text-emerald-600' : 'text-slate-500'}`}>
                      - Include at least 1 number or symbol
                    </div>
                    <div className={`text-xs flex items-center gap-1.5 transition-colors ${formData.password.length > 0 && isMinLength ? 'text-emerald-600' : 'text-slate-500'}`}>
                      - Be at least 8 characters long
                    </div>
                    <div className={`text-xs flex items-center gap-1.5 transition-colors ${formData.password.length > 0 && noSpacesOrPipe ? 'text-emerald-600' : 'text-slate-500'}`}>
                      - Cannot contain spaces and "|" symbol
                    </div>
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={!canSubmit}
                      className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-white transition-colors bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Creating Account...
                        </>
                      ) : (
                        'Create Account'
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleVerifyOtp} className="space-y-5 animate-fade-in">
                  <div className="text-center">
                    <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 text-indigo-600 bg-indigo-100 rounded-full">
                      <ShieldCheck className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">Verify Email Address</h3>
                    <p className="mt-1 text-sm text-slate-600">
                      Enter the 6-digit code sent to <span className="font-semibold">{formData.email}</span>
                    </p>
                  </div>

                  <div className="flex justify-center gap-2 py-4">
                    {otp.map((digit, i) => (
                      <input
                        key={i}
                        ref={(el) => (otpRefs.current[i] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(i, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(i, e)}
                        onPaste={i === 0 ? handleOtpPaste : undefined}
                        className={`w-12 h-14 text-center text-xl font-bold rounded-lg border-2 outline-none transition-all duration-200
                          ${digit ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-white text-slate-800'}
                          focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500/20`}
                      />
                    ))}
                  </div>

                  {otpError && (
                    <div className="p-3 text-sm text-center text-red-600 border border-red-100 rounded-lg bg-red-50">
                      {otpError}
                    </div>
                  )}

                  <div className="flex flex-col gap-3 pt-2">
                    <Button
                      type="submit"
                      className="w-full text-white bg-indigo-600 hover:bg-indigo-700 h-11"
                      disabled={otpLoading || otp.some((d) => !d)}
                    >
                      {otpLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        'Verify Account'
                      )}
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleResendOtp}
                      disabled={otpLoading}
                      className="w-full text-slate-600 border-slate-200 hover:bg-slate-50 h-11"
                    >
                      Resend Code
                    </Button>

                    <button
                      type="button"
                      onClick={() => {
                        setStep('create');
                        setStatus({ type: null, message: '' });
                      }}
                      className="text-sm text-slate-500 hover:text-indigo-600"
                    >
                      Cancel and create a different account
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
